import { Product, UnboundProductData, UnboundProductOptionData } from "@/products/product.base.js";
import { InitSelectorProduct } from "@/products/product.init-selector.js";
import { STORE_LOCALE } from "@/store.constants";
import type { ProductSelector } from "@/store.js";
import { FetchedProduct, INGNORE_CAMPAIGN, Provider, ProviderData } from "./provider.base.js";

export interface FetchResponse {
  code: number;
  data: {
    product: ProductData;
    config : ConfigData;
  };
}

export interface ProductData {
  product_id    : string;
  product_name  : string;
  product_type  : string;
  product_alias : string;
  product_active: string;
  base_uri      : string;
  variations    : VariationsData;
}

export type VariationsData = Record<string, Record<string, VariationData>>;

export interface VariationData {
  product_id               : string;
  region_id                : string;
  variation_id             : string;
  platform_id              : string;
  platform_product_id      : string;
  price                    : string;
  currency_id              : string;
  in_selector              : string;
  active_platform          : string;
  variation_active         : string;
  avangate_variation_prefix: string;
  variation                : VariationDetail;
  currency_label           : string;
  currency_iso             : string;
  discount?                : DiscountData;
  promotion?               : string;
  promotion_functions?     : string;
}

export interface VariationDetail {
  variation_id   : string;
  variation_name : string;
  dimension_id   : string;
  dimension_value: string;
  years          : string;
}

export interface DiscountData {
  discounted_price: string;
  discount_value  : string;
  discount_type   : number;
}

export interface ConfigData {
  country_code: string;
  extra_params: {
    pid: string | null;
  };
}

const countriesMapping = new Map([
  ["gb", "uk"],
  ["ch", "de"],
  ["at", "de"],
  ["us", "us"],
  ["mx", "en"],
  ["nz", "au"]
]);

const getCountry = (country: string) => countriesMapping.get(country) ?? country;

export class InitSelectorProvider extends Provider {
  private country : string;
  private language: string;

  constructor(config: ProviderData) {
    super(config)
    const [language, country] = this.store[STORE_LOCALE].split("-");
    this.language = language;
    this.country = getCountry(country);
  }

  protected _getProduct(product: UnboundProductData | undefined): Product | undefined {
    return product ? new InitSelectorProduct({ ...product, store: this.store }) : product;
  }

  async fetch({ id, campaign }: ProductSelector): Promise<FetchedProduct | undefined> {
    const adaptedId = (await this.adaptTo({ id })).id;
    const computedCampaign = await this.getCampaign(adaptedId, campaign);
    const payload = this.buildPayload(id, computedCampaign);
    const apiURL = this.buildApiURL();

    let fetchResponse: FetchResponse;
    try {
      const formData = new FormData();
      formData.append("data", payload);
      const response = await fetch(apiURL.href, {
        method: "post",
        body  : formData
      });
      if (!response.ok) return undefined;

      fetchResponse = await response.json();

      // Ensure variations exist.
      if (!fetchResponse.data.product.variations ||
          Object.keys(fetchResponse.data.product.variations).length === 0) {
        return undefined;
      }
    } catch (error) {
      console.error("Failed to fetch product:", error);
      return undefined;
    }

    // Process variations into option data.
    const { options, platformId, currency } = await this.processVariations(
      id,
      fetchResponse.data.product.variations,
      computedCampaign
    );

    // Return an UnboundProductData.
    return {
      overrides: {
        oldCampaign: campaign,
        newCampaign: computedCampaign
      },
      product: {
        id      : adaptedId,
        alias   : id,
        name    : fetchResponse.data.product.product_name,
        campaign: computedCampaign,
        currency,
        platformId,
        options
      }
    }
  }

  private buildPayload(id: string, campaign?: string): string {
    const config: {
      country_code      : string,
      ignore_promotions?: boolean
      extra_params?: {
        pid: string | null
      }
    } = { country_code: this.country };

    if (INGNORE_CAMPAIGN.includes(String(campaign))) {
      config.ignore_promotions = true;
    } else {
      config.extra_params = { pid: campaign || null };
    }

    return JSON.stringify({
      ev        : 1,
      product_id: id,
      config
    });
  }

  private buildApiURL(): URL {
    const url = new URL("https://www.bitdefender.com/site/Store/ajax");
    url.searchParams.set("force_country", this.country);
    return url;
  }

  private async processVariations(
    id: string,
    variations: Record<string, Record<string, VariationData>>,
    campaign?: string
  ): Promise<{ options: Map<string, UnboundProductOptionData>; platformId: string; currency: string }> {
    const options = new Map<string, UnboundProductOptionData>();
    let platformId = "";
    let currency = "";

    // Loop through devices and subscriptions.
    for (const [devices, devicesValue] of Object.entries(variations)) {
      for (const [subscription, subscriptionsValue] of Object.entries(devicesValue)) {
        const adaptedVariation = await this.adaptTo({
          id,
          subscription: Number(subscription),
          devices     : Number(devices)
        });
        const variationData: UnboundProductOptionData = {
          devices        : adaptedVariation.devices,
          subscription   : adaptedVariation.subscription,
          price          : Number(subscriptionsValue.price),
          discountedPrice: Number(subscriptionsValue.price === subscriptionsValue.discount?.discounted_price ?  NaN : subscriptionsValue.discount?.discounted_price),
          buyLink        : "", // To be computed below.,
          bundle         : []
        };

        // Capture common info.
        platformId = subscriptionsValue.platform_product_id;
        currency = subscriptionsValue.currency_iso;
        campaign = campaign || subscriptionsValue.promotion;

        //compute buy link
        variationData.buyLink = this.buildBuyLink(id, variationData, subscriptionsValue, campaign);

        options.set(`${adaptedVariation.devices}-${adaptedVariation.subscription}`, variationData);
      }
    }
    return { options, platformId, currency };
  }

  private buildBuyLink(id: string, variation: { devices: number; subscription: number }, subscriptionsValue: VariationData, campaign?: string): string {
    const buyLinkRouteParams = ["buy", id, variation.devices, variation.subscription].join("/");
    // Build base URL; include campaign pid if available.
    const pathSuffix = campaign ? `pid.${campaign}` : "";
    const url = new URL(`https://www.bitdefender.com/site/Store/${buyLinkRouteParams}/${pathSuffix}`);
    url.searchParams.set("CURRENCY", subscriptionsValue.currency_iso);
    url.searchParams.set("DCURRENCY", subscriptionsValue.currency_iso);
    url.searchParams.set("CART", "1");
    url.searchParams.set("CARD", "2");
    url.searchParams.set("SHORT_FORM", "1");
    url.searchParams.set("LANG", this.language);
    url.searchParams.set("force_country", this.country);
    return url.href;
  }
}
